import React from 'react';
import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {Icon} from '@udemy-v2/icons';
import {BlockList} from '@udemy-v2/react-core-components';
import classNames from 'classnames';

import {
    CourseIncentiveImpressionEvent,
    CourseIncentivesImpressionEvent,
    IncentivesPlacement,
    LearningProduct,
} from '@/features/course-landing-page/events/events';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {CourseIncentive, useCourseIncentives} from './hooks/use-course-incentives.react-component';

import styles from './course-includes.module.scss';

export enum CourseIncludesPlacement {
    SIDEBAR = 'sidebar',
    BODY = 'body',
}

interface CourseIncludesProps {
    className?: string;
    placement?: CourseIncludesPlacement;
}

export const CourseIncludes = ({
    className,
    placement = CourseIncludesPlacement.SIDEBAR,
}: CourseIncludesProps) => {
    const {gettext} = useI18n();
    const incentiveItems = useCourseIncentives();
    const course = useClpCourse();
    const isMobile = useMatchMedia('sm-max');
    const isPlacementBody = placement === CourseIncludesPlacement.BODY;

    if (!incentiveItems || incentiveItems.length === 0) {
        return null;
    }

    const handleImpression = () => {
        if (!course?.id) {
            return;
        }

        publishEvent(
            new CourseIncentivesImpressionEvent({
                courseTrackingId: course.frontendTrackingId ?? '',
                uiRegion:
                    placement === CourseIncludesPlacement.BODY
                        ? IncentivesPlacement.BODY
                        : IncentivesPlacement.SIDEBAR,
                learningProduct: {
                    type: LearningProduct.COURSE,
                    id: parseInt(course.id, 10),
                    trackingId: course.frontendTrackingId ?? '',
                },
            }),
        );
    };

    const handleIncentiveImpression = (
        incentive: CourseIncentive<string | number | boolean>,
        position: number,
    ) => {
        if (!course?.id) {
            return;
        }

        publishEvent(
            new CourseIncentiveImpressionEvent({
                courseId: parseInt(course.id, 10),
                courseTrackingId: course.frontendTrackingId ?? '',
                incentiveType: incentive.type!,
                position: position,
                incentiveValue: String(incentive.value) || '',
            }),
        );
    };

    // Determine if we should use a two-column layout based on placement and device
    // Only use double column when in body placement and not on mobile
    const hasDoubleColumn = placement === CourseIncludesPlacement.BODY && !isMobile;

    // Calculate the split point for the lists
    const halfLength = Math.ceil(incentiveItems.length / 2);

    // Create the lists for a two-column layout if needed
    const firstList = hasDoubleColumn ? incentiveItems.slice(0, halfLength) : [];
    const secondList = hasDoubleColumn ? incentiveItems.slice(halfLength) : [];

    const renderIncentivesList = (
        incentives: CourseIncentive<string | number | boolean>[],
        positionOffset = 0,
    ) => (
        <BlockList className={styles['incentives-list']} size="small" padding="tight">
            {incentives.map((incentive, index) => (
                <BlockList.Item
                    key={`incentive-${index + positionOffset}`}
                    icon={
                        <Icon
                            glyph={incentive.iconGlyph!}
                            size={isPlacementBody ? 'small' : 'xsmall'}
                            color="neutral"
                            label={false}
                            aria-hidden="true"
                        />
                    }
                >
                    <TrackImpression
                        trackFunc={() =>
                            handleIncentiveImpression(incentive, index + positionOffset)
                        }
                        data-testid={`incentive-impression-${index + positionOffset}`}
                    >
                        <span className={isPlacementBody ? 'ud-text-md' : 'ud-text-sm'}>
                            {incentive.text}
                        </span>
                    </TrackImpression>
                </BlockList.Item>
            ))}
        </BlockList>
    );

    return (
        <div
            className={classNames(
                !isPlacementBody && styles['course-includes-wrapper-sidebar'],
                className,
            )}
        >
            <TrackImpression trackFunc={handleImpression} data-testid="course-includes-impression">
                <div
                    className={classNames(
                        isPlacementBody
                            ? styles['course-includes-body']
                            : styles['course-includes-sidebar'],
                    )}
                    data-testid="course-includes"
                >
                    <h2
                        className={classNames(
                            styles['course-heading'],
                            isPlacementBody ? 'ud-heading-xl' : 'ud-heading-md',
                        )}
                    >
                        {gettext('This course includes:')}
                    </h2>
                    <div className={styles['incentives-container']}>
                        {hasDoubleColumn ? (
                            <div
                                className={styles['double-list-container']}
                                data-testid="double-list-container"
                            >
                                {renderIncentivesList(firstList, 0)}
                                {renderIncentivesList(secondList, halfLength)}
                            </div>
                        ) : (
                            renderIncentivesList(incentiveItems)
                        )}
                    </div>
                </div>
            </TrackImpression>
        </div>
    );
};
